import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

import { useIndex } from "@/api/orval/auth/auth";
import { mintToken, refreshToken as refreshTokenMethod } from "@/api/orval/mcp-auth/mcp-auth";
import { useTranslation } from "@/core/i18n";
import {
  CUSTOM_REPEATMD_ENV,
  CUSTOM_USER_CREATED_AT_PROP_NAME,
  formatName,
  formatTimestamp,
  initIntercomWidget,
  INTERCOM_HIDE_TRIGGER_EVENT,
  INTERCOM_SHOW_TRIGGER_EVENT,
  onIntercomShow,
  setIntercomAuthTokens,
  SUPPORT_CHAT_API_BASE,
  SUPPORT_CHAT_APP_ID,
  SUPPORT_CHAT_TRIGGER_ID,
  updateSupportChatWidget,
} from "@/modules/support";
import { Transition } from "atoms/Transition";
import { queueTaskInBackground } from "utils/queueTaskInBackground";

const RM_JWT_TOKEN = "rm_jwt";

const PLATFORM_ID_REGEX = /\/admin\/[^/]+\/([0-9a-f-]{36})(?:\/|$)/i;

const REFRESH_SAFETY_BUFFER_MS = 90_000;

const readPlatformIdFromPath = (pathname: string): string | undefined => {
  const match = PLATFORM_ID_REGEX.exec(pathname);
  return match?.[1];
};

type IntercomWidgetProps = {
  platformId?: string;
};

export const IntercomWidget = ({ platformId: platformIdFromContext }: IntercomWidgetProps) => {
  const { t } = useTranslation();
  const [isWidgetLoaded, setIsWidgetLoaded] = useState(false);
  const [isWidgetTriggerShown, setIsWidgetTriggerShown] = useState(
    Boolean(SUPPORT_CHAT_APP_ID && SUPPORT_CHAT_API_BASE),
  );
  const [isIntercomProfileReady, setIsIntercomProfileReady] = useState(false);
  const [isMcpAuthTokenReady, setIsMcpAuthTokenReady] = useState(false);

  const { data: appData } = useIndex({ query: { staleTime: Infinity } });

  const { pathname } = useLocation();
  const platformIdFromPath = useMemo(() => readPlatformIdFromPath(pathname), [pathname]);
  const platformId = platformIdFromContext ?? platformIdFromPath;

  const currentTokenRef = useRef<string | null>(null);
  const expiresAtRef = useRef<number | null>(null);
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const refreshTokenRef = useRef<(pid: string) => Promise<void>>(null);

  // Writes this tab's token into the shared Intercom auth-token slot. Every environment's MCP
  // server reads the same workspace-level "rm_jwt" slot (the binding is fixed at MCP-server
  // creation) and the slot is last-writer-wins across env tabs, so ONLY the tab the user is
  // looking at may write it: a hidden tab pushing its refreshed token would clobber the token
  // of the env the user is actually chatting with, and the receiving AdminMcp would reject it
  // (each env verifies with its own Jwt:SignKey).
  const pushTokensToIntercom = useCallback(() => {
    const token = currentTokenRef.current;
    if (!token || document.visibilityState !== "visible") {
      return;
    }

    setIntercomAuthTokens({ [RM_JWT_TOKEN]: token });
  }, []);

  const applyToken = useCallback(
    (token: string, expiresAtUtc: string) => {
      currentTokenRef.current = token;
      expiresAtRef.current = new Date(expiresAtUtc).getTime();
      pushTokensToIntercom();
      setIsMcpAuthTokenReady(true);
    },
    [pushTokensToIntercom],
  );

  const scheduleRefresh = useCallback((pid: string) => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }

    const expiresAt = expiresAtRef.current;
    if (!expiresAt) return;

    const delayMs = Math.max(expiresAt - Date.now() - REFRESH_SAFETY_BUFFER_MS, 0);

    refreshTimerRef.current = setTimeout(() => {
      void refreshTokenRef.current?.(pid);
    }, delayMs);
  }, []);

  const refreshToken = useCallback(
    async (pid: string) => {
      const token = currentTokenRef.current;
      if (!token) return;

      try {
        const result = await refreshTokenMethod({ token });
        if (!result.token || !result.expiresAtUtc) return;
        applyToken(result.token, result.expiresAtUtc);
        scheduleRefresh(pid);
      } catch {
        try {
          const result = await mintToken({ platformId: pid });
          if (!result.token || !result.expiresAtUtc) return;
          applyToken(result.token, result.expiresAtUtc);
          scheduleRefresh(pid);
        } catch {
          console.warn(
            "[IntercomWidget] MCP auth token refresh and re-mint both failed; Fin keeps presenting the previous (possibly invalid) token until the page reloads",
          );
        }
      }
    },
    [applyToken, scheduleRefresh],
  );

  refreshTokenRef.current = refreshToken;

  useEffect(() => {
    if (!SUPPORT_CHAT_APP_ID || !SUPPORT_CHAT_API_BASE) {
      return;
    }

    const intercomGlobal = globalThis as unknown as { Intercom?: unknown };
    if (typeof intercomGlobal.Intercom === "function") {
      if (!isWidgetLoaded) {
        setIsWidgetLoaded(true);
      }
      return;
    }

    if (isWidgetLoaded) {
      return;
    }

    const intercomWidgetScript = document.createElement("script");
    intercomWidgetScript.innerText = `(function(){var w=window;var ic=w.Intercom;if(typeof ic==="function"){ic('reattach_activator');ic('update',w.intercomSettings);}else{var d=document;var i=function(){i.c(arguments);};i.q=[];i.c=function(args){i.q.push(args);};w.Intercom=i;var l=function(){var s=d.createElement('script');s.type='text/javascript';s.async=true;s.src='https://widget.intercom.io/widget/${SUPPORT_CHAT_APP_ID}';var x=d.getElementsByTagName('script')[0];x.parentNode.insertBefore(s,x);};if(document.readyState==='complete'){l();}else if(w.attachEvent){w.attachEvent('onload',l);}else{w.addEventListener('load',l,false);}}})();`;

    queueTaskInBackground(() => {
      globalThis.requestAnimationFrame(() => {
        document.body.appendChild(intercomWidgetScript);

        setIsWidgetLoaded(true);
      });
    });
  }, [isWidgetLoaded]);

  useEffect(() => {
    if (!isWidgetLoaded) {
      return;
    }

    const userData = appData?.user;
    if (!userData) {
      return;
    }

    // `infrastructureEnvironment` is the canonical PascalCase value of the backend's
    // `IRepeatMdHostResolverService.InfrastructureEnvironment` enum (LocalDev, Snap, Dev,
    // Sandbox, Demo, Production). It's deterministic across developer laptops because every
    // `ASPNETCORE_ENVIRONMENT=Development` host resolves it from the committed
    // `appsettings.AllHosts.Development.json`. Pushed into Intercom as `custom_repeatmd_env`
    // so audience rules can target an env-scoped cohort (e.g. `LocalDev`-only test bots).
    const infrastructureEnvironment = appData?.environment?.infrastructureEnvironment ?? undefined;
    const intercomUserData: Intercom_.IntercomSettings = {
      ...formatName(userData.firstName, userData.lastName),
      user_id: userData.id ?? undefined,
      email: userData.email ?? undefined,
      phone: userData.phoneNumber ?? undefined,
      created_at: formatTimestamp(userData.createdAt),
      user_hash: userData.supportChatHash ?? undefined,
      [CUSTOM_USER_CREATED_AT_PROP_NAME]: formatTimestamp(userData.createdAt),
      [CUSTOM_REPEATMD_ENV]: infrastructureEnvironment,
    };

    initIntercomWidget(intercomUserData);
    updateSupportChatWidget(intercomUserData);
    setIsIntercomProfileReady(true);
  }, [isWidgetLoaded, appData?.user, appData?.environment?.infrastructureEnvironment]);

  useEffect(() => {
    if (!isWidgetLoaded || !platformId) {
      return;
    }

    let cancelled = false;
    setIsMcpAuthTokenReady(false);

    // The token refs are scoped to the previous platform. Clear them before the new mint so a
    // platform switch whose mint is still in flight (or fails) cannot push or refresh the old
    // platform's token: pushTokensToIntercom, refreshToken, and scheduleRefresh all bail on an
    // empty ref, so nothing stale reaches Intercom until applyToken repopulates them for this
    // platform. Without this, a visibilitychange/onShow between platforms would broadcast the
    // wrong platform's JWT (Fin would then operate on the previous platform).
    currentTokenRef.current = null;
    expiresAtRef.current = null;

    void (async () => {
      try {
        const result = await mintToken({ platformId });
        if (cancelled || !result.token || !result.expiresAtUtc) return;
        applyToken(result.token, result.expiresAtUtc);
        scheduleRefresh(platformId);
      } catch {
        console.warn(
          "[IntercomWidget] initial MCP auth token mint failed; Fin data connectors will have no token for this session",
        );
      }
    })();

    return () => {
      cancelled = true;
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
    };
  }, [isWidgetLoaded, platformId, applyToken, scheduleRefresh]);

  useEffect(() => {
    if (!isWidgetLoaded || !platformId) return;

    const handleVisibilityChange = () => {
      if (document.visibilityState !== "visible") return;

      // Reclaim the shared workspace token slots for this env: another env's tab may have
      // overwritten them while this tab was hidden.
      pushTokensToIntercom();

      const expiresAt = expiresAtRef.current;
      if (!expiresAt) return;

      const remainingMs = expiresAt - Date.now();
      if (remainingMs < REFRESH_SAFETY_BUFFER_MS) {
        void refreshToken(platformId);
      } else {
        scheduleRefresh(platformId);
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange, {
      passive: true,
    });

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [isWidgetLoaded, platformId, pushTokensToIntercom, refreshToken, scheduleRefresh]);

  // Re-assert this env's token the moment the messenger opens — the strongest signal that the
  // user is about to chat with Fin in THIS tab's environment.
  useEffect(() => {
    if (!isWidgetLoaded || !isIntercomProfileReady) {
      return;
    }

    onIntercomShow(pushTokensToIntercom);
  }, [isWidgetLoaded, isIntercomProfileReady, pushTokensToIntercom]);

  useEffect(() => {
    const showWidgetTrigger = () => setIsWidgetTriggerShown(true);
    const hideWidgetTrigger = () => setIsWidgetTriggerShown(false);

    globalThis.addEventListener(INTERCOM_SHOW_TRIGGER_EVENT, showWidgetTrigger, {
      passive: true,
    });
    globalThis.addEventListener(INTERCOM_HIDE_TRIGGER_EVENT, hideWidgetTrigger, {
      passive: true,
    });

    return () => {
      globalThis.removeEventListener(INTERCOM_SHOW_TRIGGER_EVENT, showWidgetTrigger);
      globalThis.removeEventListener(INTERCOM_HIDE_TRIGGER_EVENT, hideWidgetTrigger);
    };
  }, []);

  const isSupportChatReady =
    isIntercomProfileReady && (platformId === undefined || isMcpAuthTokenReady);
  const canShowWidgetTrigger = isWidgetTriggerShown && isSupportChatReady;
  const supportChatLabel = t("common.openSupportChat");

  return (
    <Transition show={canShowWidgetTrigger}>
      <button
        aria-label={supportChatLabel}
        className="absolute right-5 bottom-5 z-20 hidden h-10 w-10 items-center justify-center rounded-full bg-neutral-900 transition-opacity duration-300 data-closed:opacity-0 lg:flex"
        id={SUPPORT_CHAT_TRIGGER_ID}
        title={supportChatLabel}
        type="button"
      >
        <span className="type-heading-md text-white">?</span>
      </button>
    </Transition>
  );
};
